import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { animate, useMotionValue } from 'motion/react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { IMAGE_ID_PREFIX } from '@/vdp/Gallery/ScrollableGallery';
import GalleryType from '~/constants/vdp/GalleryType';
import CarouselContext from '~/context/vdp/CarouselContext';
import ModalContext from '~/context/vdp/ModalContext';
import {
  Direction,
  EventItem,
  SCROLL_THRESHOLD,
  VISIBLE_SLIDE_COUNT
} from './constants';
import { Action, elementEngaged } from '../Analytics';

type UseGalleryScrollProps = {
  imagesCount: number;
  snapInterval: number;
  scrollInterval: number;
};

export default function useGalleryScroll({
  imagesCount,
  snapInterval,
  scrollInterval
}: UseGalleryScrollProps) {
  const { trackEvent } = useContext(AnalyticsContext);
  const { slideImgId } = useContext(CarouselContext);
  const { isFullScreenGalleryOpen } = useContext(ModalContext);
  const scrollPercent = useMotionValue(0);
  const galleryRef = useRef<HTMLDivElement>(null);
  const scrollEventData = useRef<{
    action: Action;
    actionTaken?: Direction;
    item: EventItem;
    isModal: boolean;
    galleryType: GalleryType;
    section: string;
  }>({
    action: Action.Scrolled,
    item: EventItem.Carousel,
    isModal: false,
    galleryType: GalleryType.Tile,
    section: 'Gallery'
  });
  const previousScrollLeft = useRef<number>(0);
  const dragStartScrollLeft = useRef<number>(0);
  const isScrolling = useRef<boolean>(false);
  const isDraggingRef = useRef<boolean>(false);
  const isProgrammaticScroll = useRef<boolean>(false);

  const [containerWidth, setContainerWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(true);
  const [shouldLoadAllImages, setShouldLoadAllImages] = useState(false);

  // Keep ref in sync with state for stable callback references
  isDraggingRef.current = isDragging;

  const thumbWidth =
    imagesCount > 0
      ? (VISIBLE_SLIDE_COUNT / imagesCount) * containerWidth
      : containerWidth;

  const calculateDirection = useCallback(
    (currentPosition: number, targetPosition: number): Direction => {
      return targetPosition > currentPosition ? Direction.Next : Direction.Prev;
    },
    []
  );

  const calculateScrollPercent = useCallback((gallery: HTMLDivElement) => {
    const maxScroll = gallery.scrollWidth - gallery.clientWidth;

    return maxScroll > 0 ? gallery.scrollLeft / maxScroll : 0;
  }, []);

  const getSnapElements = useCallback(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;

    return Array.from(gallery.children).filter(
      (child, index): child is HTMLElement =>
        child instanceof HTMLElement && index % snapInterval === 0
    );
  }, [snapInterval]);

  const isDefaultScrollState = useCallback(() => {
    return (
      scrollEventData.current.action === Action.Scrolled &&
      scrollEventData.current.item === EventItem.Carousel &&
      !scrollEventData.current.actionTaken
    );
  }, []);

  const setScrollEventData = useCallback(
    (action: Action, item: EventItem, actionTaken?: Direction) => {
      scrollEventData.current = {
        action,
        actionTaken,
        item,
        isModal: false,
        galleryType: GalleryType.Tile,
        section: 'Gallery'
      };
    },
    []
  );

  const syncScrollState = useCallback(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;

    const percent = calculateScrollPercent(gallery);

    // Set scrollbar thumb position
    scrollPercent.set(percent);

    // Update button visibility based off of scroll position (batched in single state update)
    setShowLeftButton(percent > 0);
    setShowRightButton(percent < SCROLL_THRESHOLD);
  }, [calculateScrollPercent, scrollPercent]);

  const findNearestSnapPoint = useCallback(() => {
    const gallery = galleryRef.current;
    if (!gallery) return null;

    const snapElements = getSnapElements();
    if (!snapElements) return null;

    const currentScroll = gallery.scrollLeft;
    let closestPosition = 0;
    let minDistance = Infinity;

    for (const element of snapElements) {
      const distance = Math.abs(element.offsetLeft - currentScroll);

      if (distance < minDistance) {
        minDistance = distance;
        closestPosition = element.offsetLeft;
      }
    }

    return closestPosition;
  }, [getSnapElements]);

  const updateDimensions = useCallback(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;

    setContainerWidth(gallery.clientWidth);

    syncScrollState();
  }, [syncScrollState]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);

    // Load all images on first scrollbar drag interaction
    if (!shouldLoadAllImages) {
      setShouldLoadAllImages(true);
    }

    const gallery = galleryRef.current;
    if (!gallery) return;

    const direction = calculateDirection(
      dragStartScrollLeft.current,
      gallery.scrollLeft
    );
    setScrollEventData(Action.Dragged, EventItem.Scrollbar, direction);

    const snapPosition = findNearestSnapPoint();
    if (snapPosition === null) return;

    gallery.scrollTo({
      left: snapPosition,
      behavior: 'smooth'
    });

    syncScrollState();
  }, [
    calculateDirection,
    findNearestSnapPoint,
    setScrollEventData,
    shouldLoadAllImages,
    syncScrollState
  ]);

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
    const gallery = galleryRef.current;
    if (gallery) {
      dragStartScrollLeft.current = gallery.scrollLeft;
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (isDraggingRef.current) return;

    const gallery = galleryRef.current;
    if (!gallery) return;

    isScrolling.current = true;

    // Load all images on first scroll interaction
    if (!shouldLoadAllImages) {
      setShouldLoadAllImages(true);
    }

    const currentScrollLeft = gallery.scrollLeft;
    const scrollDifference = currentScrollLeft - previousScrollLeft.current;

    // Only set event data if in default state (user manually scrolling)
    // This prevents overwriting data from button clicks, drags, etc.
    if (isDefaultScrollState() && Math.abs(scrollDifference) > 1) {
      const direction = calculateDirection(
        previousScrollLeft.current,
        currentScrollLeft
      );
      setScrollEventData(Action.Scrolled, EventItem.Carousel, direction);
    }

    previousScrollLeft.current = currentScrollLeft;

    syncScrollState();
  }, [
    calculateDirection,
    isDefaultScrollState,
    setScrollEventData,
    shouldLoadAllImages,
    syncScrollState
  ]);

  const handleScrollEnd = useCallback(() => {
    if (isDraggingRef.current) return;

    const gallery = galleryRef.current;
    if (!gallery) return;

    isScrolling.current = false;

    if (!isProgrammaticScroll.current) {
      trackEvent(elementEngaged, scrollEventData.current);
    }

    isProgrammaticScroll.current = false;
    // Reset event custom attributes after send
    setScrollEventData(Action.Scrolled, EventItem.Carousel);

    syncScrollState();
  }, [setScrollEventData, syncScrollState, trackEvent]);

  const handleScrollPercentChange = useCallback(
    (percent: number, smooth = false) => {
      const gallery = galleryRef.current;
      if (!gallery) return;

      const maxScroll = gallery.scrollWidth - gallery.clientWidth;
      const targetScrollLeft = maxScroll * percent;

      if (smooth) {
        const direction = calculateDirection(
          gallery.scrollLeft,
          targetScrollLeft
        );
        setScrollEventData(Action.Clicked, EventItem.Scrollbar, direction);

        gallery.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth'
        });
      } else {
        // Programmatic scroll (dragging scrollbar thumb): instant scroll + animate progress
        gallery.scrollLeft = targetScrollLeft;
        isProgrammaticScroll.current = true;

        animate(scrollPercent, percent, {
          type: 'tween',
          duration: 0.05,
          ease: 'linear'
        });
      }
    },
    [calculateDirection, scrollPercent, setScrollEventData]
  );

  const scrollToSnapPoint = useCallback(
    (direction: Direction) => {
      const gallery = galleryRef.current;
      if (!gallery) return;

      setScrollEventData(Action.Clicked, EventItem.ArrowButton, direction);

      const allImages = Array.from(gallery.children) as HTMLElement[];
      if (allImages.length === 0) return;

      const galleryRect = gallery.getBoundingClientRect();
      const currentScroll = gallery.scrollLeft;

      // Find current image index by finding the closest image to current scroll position
      // Using getBoundingClientRect for better Safari compatibility
      let currentIndex = 0;
      let minDistance = Infinity;

      allImages.forEach((img, index) => {
        const imgRect = img.getBoundingClientRect();
        const imgScrollPosition =
          imgRect.left - galleryRect.left + currentScroll;
        const distance = Math.abs(imgScrollPosition - currentScroll);

        if (distance < minDistance) {
          minDistance = distance;
          currentIndex = index;
        }
      });

      // Calculate target image index based on scroll interval
      const targetImageIndex =
        direction === Direction.Next
          ? Math.min(currentIndex + scrollInterval, allImages.length - 1)
          : Math.max(currentIndex - scrollInterval, 0);

      const targetImage = allImages[targetImageIndex];
      const targetRect = targetImage.getBoundingClientRect();
      const targetScrollPosition =
        targetRect.left - galleryRect.left + currentScroll;

      gallery.scrollTo({
        left: targetScrollPosition,
        behavior: 'smooth'
      });
    },
    [scrollInterval, setScrollEventData]
  );

  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;

    updateDimensions();
    gallery.addEventListener('scroll', handleScroll);
    gallery.addEventListener('scrollend', handleScrollEnd);
    window.addEventListener('resize', updateDimensions);

    return () => {
      gallery.removeEventListener('scroll', handleScroll);
      gallery.removeEventListener('scrollend', handleScrollEnd);
      window.removeEventListener('resize', updateDimensions);
    };
  }, [handleScroll, handleScrollEnd, updateDimensions]);

  useEffect(() => {
    if (!isFullScreenGalleryOpen && galleryRef.current) {
      const targetSlide = galleryRef.current.querySelector(
        `#${IMAGE_ID_PREFIX}-${slideImgId}`
      );

      if (targetSlide) {
        isProgrammaticScroll.current = true;
        targetSlide.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }
    }
  }, [isFullScreenGalleryOpen, slideImgId]);

  return {
    galleryRef,
    handleDragEnd,
    handleDragStart,
    handleScrollPercentChange,
    isDragging,
    scrollPercent,
    scrollToSnapPoint,
    shouldLoadAllImages,
    showLeftButton,
    showRightButton,
    thumbWidth
  };
}
